import { useEffect, useMemo, useState } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import type { HeatmapAspectBreakdownRow } from '../external-monitoring-aggregate';
import {
  GREEN_PALETTE,
  RED_PALETTE,
  YELLOW_PALETTE,
} from './heatmap-sentiment-palettes';
import { HeatmapCategoryFooter } from './heatmap-category-footer';

export type HeatmapApi = {
  categories: string[];
  categoryIds: string[];
  counts: [number, number, number][];
  aspectsByCategoryId: Record<string, HeatmapAspectBreakdownRow[]>;
};

const ENCODE_BASE = [0, 101, 202] as const;

const emptyHeatmap: HeatmapApi = {
  categories: [],
  categoryIds: [],
  counts: [],
  aspectsByCategoryId: {},
};

/** Округление долей до целых процентов с суммой ровно 100 (метод наибольших остатков). */
function roundPercentsTo100(neg: number, neu: number, pos: number): [number, number, number] {
  const sum = neg + neu + pos;
  if (sum === 0) return [0, 0, 0];
  const exact = [(neg / sum) * 100, (neu / sum) * 100, (pos / sum) * 100];
  const floored = exact.map((v) => Math.floor(v)) as [number, number, number];
  const remainder = 100 - floored[0] - floored[1] - floored[2];
  const byFrac = [0, 1, 2].map((i) => ({ i, f: exact[i] - floored[i] }));
  byFrac.sort((a, b) => b.f - a.f);
  const out: [number, number, number] = [...floored];
  for (let k = 0; k < remainder; k++) {
    out[byFrac[k].i]++;
  }
  return out;
}

function decodeEncodedChartValue(val: number): number {
  if (val <= 100) return Math.round(val);
  if (val <= 201) return Math.round(val - 101);
  return Math.round(val - 202);
}

/** Диапазоны цветов: оттенок колонки (нег/нейт/поз), насыщенность растёт с долей. */
function sentimentIntensityRanges(
  base: number,
  palette: readonly string[],
): { from: number; to: number; color: string; foreColor: string }[] {
  const n = palette.length;
  const step = 100 / n;
  return palette.map((color, i) => {
    const from = base + i * step;
    const to = i === n - 1 ? base + 100 : base + (i + 1) * step - 0.001;
    const foreColor = i < n - 2 ? '#111827' : '#fff';
    return { from, to, color, foreColor };
  });
}

const HEATMAP_COLOR_RANGES = [
  ...sentimentIntensityRanges(0, RED_PALETTE),
  ...sentimentIntensityRanges(101, YELLOW_PALETTE),
  ...sentimentIntensityRanges(202, GREEN_PALETTE),
];

const ALL_SENTIMENT_LABELS = ['Негативно', 'Нейтрально', 'Позитивно'];
type HeatmapSortKey = 'none' | 'negative' | 'neutral' | 'positive';
type HeatmapSortDir = 'desc' | 'asc';

export const useHeatmapCategorySentiment = (
  sentimentKey?: 'all' | 'posNeg' | 'positive' | 'neutral' | 'negative',
  api?: HeatmapApi,
  _period?: ExternalMonitoringPeriodBounds,
): ChartProps => {
  const [drillCategoryId, setDrillCategoryId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<HeatmapSortKey>('none');
  const [sortDir, setSortDir] = useState<HeatmapSortDir>('desc');

  const resetKey = api
    ? `${api.categoryIds?.join(',')}|${api.categories.join('\u0001')}`
    : '';

  useEffect(() => {
    setDrillCategoryId(null);
  }, [resetKey]);

  const colIndexes: (0 | 1 | 2)[] = useMemo(() => {
    if (sentimentKey === 'negative') return [0];
    if (sentimentKey === 'neutral') return [1];
    if (sentimentKey === 'positive') return [2];
    if (sentimentKey === 'posNeg') return [0, 2];
    return [0, 1, 2];
  }, [sentimentKey]);

  const sentimentLabels = useMemo(() => colIndexes.map((i) => ALL_SENTIMENT_LABELS[i]), [colIndexes]);

  const chartData = useMemo<ReactApexChartProps>(() => {
    const src = api ?? emptyHeatmap;

    type Row = { id: string; label: string; counts: [number, number, number] };
    let rows: Row[] = [];

    if (drillCategoryId != null) {
      const aspects = src.aspectsByCategoryId[drillCategoryId] ?? [];
      rows = aspects.map((a) => ({
        id: String(a.aspectId),
        label: a.name,
        counts: [a.negative, a.neutral, a.positive],
      }));
    } else {
      rows = src.categories.map((name, i) => ({
        id: src.categoryIds[i] ?? String(i),
        label: name,
        counts: src.counts[i] ?? [0, 0, 0],
      }));
    }

    if (sortKey !== 'none') {
      const sentimentIdx = sortKey === 'negative' ? 0 : sortKey === 'neutral' ? 1 : 2;
      rows = [...rows].sort((a, b) => {
        const diff = (b.counts[sentimentIdx] ?? 0) - (a.counts[sentimentIdx] ?? 0);
        if (diff !== 0) return sortDir === 'desc' ? diff : -diff;
        return a.label.localeCompare(b.label, 'ru');
      });
    }

    const percentsFull = rows.map((r) =>
      roundPercentsTo100(r.counts[0], r.counts[1], r.counts[2]),
    );

    const countsVisible: number[][] = rows.map((r) =>
      colIndexes.map((ci) => r.counts[ci] ?? 0),
    );

    const series = rows.map((r, i) => ({
      name: r.label,
      data: colIndexes.map((globalCol) => {
        const p = percentsFull[i][globalCol] ?? 0;
        return ENCODE_BASE[globalCol] + p;
      }),
    }));

    const chartHeightPx = Math.min(720, Math.max(280, 100 + rows.length * 40));

    return {
      series,
      height: `${chartHeightPx}px`,
      options: {
        chart: {
          type: 'heatmap',
          toolbar: { show: false },
          events: {
            click: (_event, _ctx, cfg) => {
              const si = (cfg as { seriesIndex?: number }).seriesIndex;
              if (typeof si !== 'number') return;
              if (drillCategoryId != null) return;
              const id = rows[si]?.id;
              if (id) setDrillCategoryId(id);
            },
            xAxisLabelClick: (_event, _chartContext, opts: { labelIndex?: number }) => {
              const labelIndex = opts?.labelIndex ?? -1;
              if (labelIndex < 0) return;
              const col = colIndexes[labelIndex];
              const clickedKey: HeatmapSortKey =
                col === 0 ? 'negative' : col === 1 ? 'neutral' : col === 2 ? 'positive' : 'none';
              if (clickedKey === 'none') return;
              if (sortKey !== clickedKey) {
                setSortKey(clickedKey);
                setSortDir('desc');
                return;
              }
              if (sortDir === 'desc') {
                setSortDir('asc');
                return;
              }
              setSortKey('none');
              setSortDir('desc');
            },
          },
        },
        plotOptions: {
          heatmap: {
            radius: 4,
            enableShades: false,
            colorScale: {
              ranges: HEATMAP_COLOR_RANGES,
            },
          },
        },
        xaxis: {
          categories: sentimentLabels.map((label, idx) => {
            const col = colIndexes[idx];
            const key: HeatmapSortKey =
              col === 0 ? 'negative' : col === 1 ? 'neutral' : col === 2 ? 'positive' : 'none';
            if (sortKey !== key) return label;
            return `${label} ${sortDir === 'desc' ? '↓' : '↑'}`;
          }),
          position: 'top',
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${decodeEncodedChartValue(val)}%`,
        },
        tooltip: {
          custom: ({
            seriesIndex,
            dataPointIndex,
          }: {
            seriesIndex: number;
            dataPointIndex: number;
          }) => {
            const row = countsVisible[seriesIndex];
            const n = row?.[dataPointIndex];
            if (n === undefined) return '';
            const label = sentimentLabels[dataPointIndex] ?? '';
            const name = rows[seriesIndex]?.label ?? '';
            return (
              '<div class="apexcharts-tooltip-title" style="font-family: inherit; padding: 4px 8px">' +
              name +
              '</div><div style="padding: 4px 8px">' +
              label +
              ': <strong>' +
              String(n) +
              '</strong></div>'
            );
          },
        },
        states: {
          hover: { filter: { type: 'none' } },
        },
      },
    };
  }, [api, colIndexes, sentimentLabels, drillCategoryId, sortKey, sortDir, _period?.begin, _period?.end]);

  const categoryChips = useMemo(() => {
    const src = api ?? emptyHeatmap;
    return src.categories.map((name, i) => ({
      id: src.categoryIds[i] ?? String(i),
      name,
    }));
  }, [api]);

  const footer =
    (api?.categories?.length ?? 0) > 0 && !api?.categories.every((c) => c === '—') ? (
      <HeatmapCategoryFooter
        categories={categoryChips}
        drillCategoryId={drillCategoryId}
        onShowAll={() => setDrillCategoryId(null)}
        onSelectCategory={(id) => setDrillCategoryId(id)}
      />
    ) : undefined;

  return {
    ...chartData,
    label: 'Матрица категорий',
    labelTooltip:
      'Отображает процентное распределение тональности аспектов внутри каждой категории за выбранный период. Насыщенность цвета в ячейке отражает долю оценок. Клик по строке категории раскрывает детализацию входящих в нее аспектов с аналогичным распределением тональности.',
    labelInfoIcon: true,
    type: 'heatmap',
    fullWidth: true,
    subLabel:
      drillCategoryId != null
        ? 'Аспекты выбранной категории (те же столбцы тональности).'
        : undefined,
    footer,
  };
};
